import { useState, useEffect, useCallback } from "react";
import { SessionsService, type GarminFile } from "@/services/SessionsService";

interface GarminFilesSubTabProps {
  userId: number;
  onCountChange?: (count: number) => void;
}

const formatFileSize = (bytes: number | null): string => {
  if (bytes === null || bytes === undefined) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (epoch: number | null): string => {
  if (!epoch) return "-";
  const date = new Date(epoch);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const GarminFilesSubTab: React.FC<GarminFilesSubTabProps> = ({ userId, onCountChange }) => {
  const [files, setFiles] = useState<GarminFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");

  const loadFiles = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await SessionsService.listGarminFiles(userId);
      setFiles(data);
      onCountChange?.(data.length);
    } catch (err) {
      console.error("Error loading Garmin files:", err);
      setError("Failed to load Garmin files. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [userId, onCountChange]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleDownload = async (file: GarminFile) => {
    setDownloadingId(file.activityId);
    try {
      await SessionsService.downloadGarminFile(userId, file.activityId);
    } catch (err) {
      console.error("Error downloading FIT file:", err);
      alert("Failed to download FIT file. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const filteredFiles = files.filter((file) => {
    if (statusFilter === "imported") return file.sessionId !== null;
    if (statusFilter === "not-imported") return file.sessionId === null;
    if (statusFilter === "not-surfr-app") return file.outcome === "IGNORED_NOT_SURFR_APP";
    if (statusFilter === "too-short") return file.outcome === "IGNORED_TOO_SHORT";
    if (statusFilter === "no-data") return file.outcome === "IGNORED_NO_DATA";
    if (statusFilter === "failed") return file.outcome === "FAILED";
    return true;
  });

  const importedCount = files.filter((f) => f.sessionId !== null).length;
  const orphanedCount = files.filter((f) => f.sessionId === null).length;
  const notSurfrCount = files.filter((f) => f.outcome === "IGNORED_NOT_SURFR_APP").length;
  const tooShortCount = files.filter((f) => f.outcome === "IGNORED_TOO_SHORT").length;
  const noDataCount = files.filter((f) => f.outcome === "IGNORED_NO_DATA").length;
  const failedCount = files.filter((f) => f.outcome === "FAILED").length;

  /** Why a file has no session, in words support can repeat to the rider. */
  const describeOutcome = (file: GarminFile): { label: string; detail: string; className: string } => {
    switch (file.outcome) {
      case "IGNORED_NOT_SURFR_APP":
        return {
          label: "Not a Surfr session",
          detail: `Recorded with "${file.appName ?? "unknown app"}" (${file.sportType ?? "unknown sport"}), not the Surfr watch app. Rider was not notified.`,
          className: "bg-amber-100 text-amber-800",
        };
      case "IGNORED_TOO_SHORT":
        return {
          label: "Too short / no GPS",
          detail: `${file.outcomeReason ?? "Under a minute on the water or no GPS"}. Rider was notified.`,
          className: "bg-amber-100 text-amber-800",
        };
      case "IGNORED_NO_DATA":
        return { label: "No data to upload", detail: file.outcomeReason ?? "Neither jumps nor GPS after processing.", className: "bg-amber-100 text-amber-800" };
      case "FAILED":
        return { label: "Upload failed", detail: `${file.outcomeReason ?? "Backend upload failed"}. The import is retried automatically.`, className: "bg-red-100 text-red-800" };
      default:
        return { label: "Not Imported", detail: "No outcome recorded (file predates outcome tracking).", className: "bg-red-100 text-red-800" };
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="animate-spin h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <svg className="w-12 h-12 text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <p className="text-sm text-gray-600 mb-4">{error}</p>
        <button
          onClick={loadFiles}
          className="px-4 py-2 text-sm font-medium text-cyan-600 hover:text-cyan-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-gray-500 italic">No Garmin FIT files found for this user</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with filter and summary */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-cyan-500"
          >
            <option value="">All ({files.length})</option>
            <option value="imported">Imported ({importedCount})</option>
            <option value="not-imported">Not Imported ({orphanedCount})</option>
            <option value="not-surfr-app">Not a Surfr session ({notSurfrCount})</option>
            <option value="too-short">Too short / no GPS ({tooShortCount})</option>
            <option value="no-data">No data to upload ({noDataCount})</option>
            <option value="failed">Upload failed ({failedCount})</option>
          </select>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {importedCount} imported, {orphanedCount} not imported ({notSurfrCount} not Surfr, {tooShortCount} too short, {noDataCount} no data, {failedCount} failed)
          </span>
          <button
            onClick={loadFiles}
            className="text-xs text-cyan-600 hover:text-cyan-700 font-medium"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activity ID</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Size</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Date</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Session</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredFiles.map((file) => (
              <tr key={file.activityId} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm font-mono text-gray-900">{file.activityId}</td>
                <td className="px-4 py-2 text-sm text-gray-600">{formatFileSize(file.fileSize)}</td>
                <td className="px-4 py-2 text-sm text-gray-600">{formatDate(file.fileDate)}</td>
                <td className="px-4 py-2">
                  {file.sessionId !== null ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Imported
                    </span>
                  ) : (
                    (() => {
                      const o = describeOutcome(file);
                      return (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-help ${o.className}`}
                          title={o.detail}
                        >
                          {o.label}
                        </span>
                      );
                    })()
                  )}
                </td>
                <td className="px-4 py-2 text-sm">
                  {file.sessionId !== null ? (
                    <span className="text-cyan-600 font-medium">
                      #{file.sessionId}
                      {file.sessionDate ? (
                        <span className="text-gray-400 font-normal ml-1">
                          ({formatDate(file.sessionDate)})
                        </span>
                      ) : null}
                    </span>
                  ) : (
                    <span className="text-gray-400">&mdash;</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => handleDownload(file)}
                    disabled={downloadingId === file.activityId}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-cyan-600 hover:text-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Download FIT file"
                  >
                    {downloadingId === file.activityId ? (
                      <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    )}
                    Download
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredFiles.length === 0 && (
        <div className="flex items-center justify-center py-8">
          <p className="text-sm text-gray-500 italic">No files match the current filter</p>
        </div>
      )}
    </div>
  );
};
